import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { DEBOUNCE_TIMEOUT_LONG } from '../../../../../constants';
import debounce from 'lodash/debounce';
import { injectIntl } from 'react-intl';
import messages from '../../../../../utils/translationMessages';
import { PortalConfigContext } from '../../../../../config/portal';
import { getLength as getUomAbbr } from '../../../../../utils/uomHelper.js';
import { evaluateMinMaxChanges } from '../utils';
import classnames from 'classnames';
import { Cookies } from 'react-cookie';
import { setCookie } from '../../../../../utils/cookies.js';
import { UOM_COOKIE } from '../../../../../constants/cookies/index.js';

class FilterLength extends PureComponent {
  state = {
    min: this.props.min || '',
    max: this.props.max || '',
    customUom: ''
  };

  updateMultiInput() {
    const max = this.props.max;
    const min = this.props.min;

    if (!this.state.editing) {
      this.setState({
        customUom: getUomAbbr(this.props.customUom?.length),
        max: max || '',
        min: min || ''
      });
    }
  }

  componentDidMount() {
    this.updateMultiInput();
  }

  componentDidUpdate() {
    this.updateMultiInput();
  }

  handleDataChange(type, value) {
    if (value) {
      value = String(Math.trunc(value));
    }

    this.setState((state) => ({
      ...state,
      [type]: value
    }));
    if (this.state[type] === value) {
      return;
    }

    const trackingValue = value;
    const stateMax = this.state.max;
    const stateMin = this.state.min;

    if (type === 'min') {
      if (this.props.tracking) {
        if (!trackingValue) {
          this.debounceTrackingRemove('minimum length removed');
        } else {
          this.debounceTracking({ ['length']: { 'min': trackingValue } });
        }
      }
      const { min, max } = evaluateMinMaxChanges(trackingValue, stateMax, type);
      this.props.handleDataChange('length', {
        min: this.state.editing ? trackingValue : min,
        max: this.state.editing ? stateMax : max
      });
    } else {
      if (this.props.tracking) {
        if (!trackingValue) {
          this.debounceTrackingRemove('maximum length removed');
        } else {
          this.debounceTracking({ ['length']: { 'max': trackingValue } });
        }
      }
      const { min, max } = evaluateMinMaxChanges(stateMin, trackingValue, type);
      this.props.handleDataChange('length', {
        min: this.state.editing ? stateMin : min,
        max: this.state.editing ? trackingValue : max
      });
    }
  }

  handleOnEdit(editingState, type) {
    this.setState(
      (state) => ({ ...state, editing: editingState }),
      () => {
        if (!editingState) {
          const valueToPass = type === 'min' ? this.state.min : this.state.max;
          this.handleDataChange(type, valueToPass);
        }
      }
    );
  }

  handleUomChange(uom) {
    const inOneDay = new Date();
    inOneDay.setDate(inOneDay.getDate() + 1);

    const data = {
      value: 1,
      expires: inOneDay,
      path: '/'
    };
    setCookie(this.props.cookies, UOM_COOKIE, uom, data);

    const max = this.state.max;
    const min = this.state.min;
    this.setState({ customUom: uom, max: max || '', min: min || '' });

    this.props.handleDataChange('length', { min: min, max: max });
  }

  debounceTracking = debounce(
    (value) => this.props.tracking.facetAdded(value),
    DEBOUNCE_TIMEOUT_LONG
  );
  debounceTrackingRemove = debounce(
    (value) => this.props.tracking.facetRemoved(value),
    DEBOUNCE_TIMEOUT_LONG
  );

  render() {
    const {
      intl: { formatMessage: t }
    } = this.props;
    const uomAbbr = this.state.customUom;
    const supportedUoms = Object.keys(this.context.supportedUoms);
    const placeholderMin = this.props.isThreeColumnLayout
      ? t(messages.quickSearch.min)
      : t(messages.lengthMin);
    const placeholderMax = this.props.isThreeColumnLayout
      ? t(messages.quickSearch.max)
      : t(messages.lengthMax);
    return (
      <div className="search-filter length">
        <div className="search-filter-group">
          <input
            type="number"
            aria-label="min-length-input"
            className="small length-min"
            placeholder={placeholderMin}
            value={this.state.min || ''}
            onChange={(value) =>
              this.handleDataChange('min', value.target.value)
            }
            onFocus={() => this.handleOnEdit(true, 'min')}
            onBlur={() => this.handleOnEdit(false, 'min')}
            title={t(messages.lengthMin)}
          />
          <span>{t(messages.to)}</span>
          <input
            type="number"
            aria-label="max-length-input"
            className="small length-max"
            value={this.state.max || ''}
            placeholder={placeholderMax}
            onChange={(value) =>
              this.handleDataChange('max', value.target.value)
            }
            onFocus={() => this.handleOnEdit(true, 'max')}
            onBlur={() => this.handleOnEdit(false, 'max')}
            title={t(messages.lengthMax)}
          />
          { supportedUoms.length > 1 && (
            <div className="uom-wrapper">
              {  supportedUoms.map((uom, index) => (
              <div
                className={classnames('filled-block', {
                  active: uom.includes(uomAbbr)
                })}
                onClick={() => this.handleUomChange(uom)}
                key={index}
              >
                {uom}
              </div>
            )) }
            </div>
          )}
        </div>
      </div>
    );
  }
}

FilterLength.propTypes = {
  cookies: PropTypes.instanceOf(Cookies),
  handle: PropTypes.string,
  handleDataChange: PropTypes.func.isRequired,
  initialMax: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  max: PropTypes.string,
  min: PropTypes.string,
  tracking: PropTypes.shape({
    facetAdded: PropTypes.func.isRequired,
    facetRemoved: PropTypes.func.isRequired
  }),
  customUom: PropTypes.shape({
    length: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    weight: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    speed: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    capacity: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    distance: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    radius: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    })
  }),
  isThreeColumnLayout: PropTypes.bool
};

FilterLength.contextType = PortalConfigContext;
export default injectIntl(FilterLength);
